import cn from 'classnames'
import { CrudStoreItemPartner360 } from 'legacy/lut/classes/models/partner360/CrudStoreItemPartner360'
import { IFormOfferings } from 'legacy/lut/classes/models/partner360/IFormOfferings'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { IProgramType } from 'legacy/lut/features/partner360/configuration'
import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { mapPartner360ProgramTypes } from 'legacy/lut/services/partner360/configuration/mappings/mapPartner360ProgramTypes'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation } from 'cng-common/features/translations/components/translation'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { Button } from 'core-components/library/button'
import { LoaderOverlay } from 'core-components/library/loader'
import { Logic, Operator } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'
import { selectP360PartnerFlagsIsLoading } from 'partner360/features/p360PartnerFlags'
import { requestP360PartnerFlags } from 'partner360/features/p360PartnerFlags/api/requestP360PartnerFlags'
import { EntityManagement } from 'partner360/widgets/gp360Administration/components/globalPartnerTable/components/globalPartnerDetailsContent/components/entityManagement/EntityManagement'

import { addPartnersToGlobalPartner } from '../../../../api/addPartnersToGlobalPartner'
import { IAttachedPartner } from '../../../../models/attachedPartner'
import { TRANSLATION_KEYS } from '../../../../translations'
import { AddPartnersModal } from '../../../addPartnersModal/AddPartnersModal'
import { GLOBAL_PARTNER_TYPE_OPTIONS } from '../../../globalPartnerTypeButtons/configs'
import { getJbpGlobalPartnerDetails } from '../../helpers/getJbpGlobalPartnerDetails'
import { IGlobalPartnerDetails } from '../../models/globalPartnerDetails'

import { BasicInfo } from './components/basicInfo/BasicInfo'
import { ContentTypeButtons, GlobalPartnerContentTypes } from './components/contentTypeButtons'
import { IFilters } from './components/filtersContainer'
import { FiltersContainer } from './components/filtersContainer'
import { PasteFromClipboard } from './components/pasteFromClipboard/PasteFromClipboard'
import { TableContent } from './components/tableContent/TableContent'
import { INITIAL_FILTERS } from './configs'
import { getOfferingsFilter } from './helpers/getOfferingsFilter'
import { IGlobalPartnerDetailsContentProps } from './models/globalPartnerDetailsContentProps'

import styles from './GlobalPartnerDetailsContent.module.scss'

export const GlobalPartnerDetailsContent = ({
  initialData,
  formData,
  globalPartnerCurrentFYearTargetsNet,
  globalPartnerCurrentFYearTargetsTNet,
  globalPartnerCurrentFYearTargetsUTNet,
  globalPartnerCurrentFYearTargetsNdp,
  isNewGlobalPartner,
  isValid,
  fYear,
  globalPartnerType,
  onClose,
  onTabChange,
  valueType,
  onChangeValueType,
  hqPartnerId,
}: IGlobalPartnerDetailsContentProps) => {
  /** -------------------- Selectors ----------------------- */
  const { currentFYear } = useSelector(selectJBPSettings)
  const isFlagsLoading = useSelector(selectP360PartnerFlagsIsLoading)

  /** -------------------- Variables ----------------------- */
  const [contentType, setContentType] = useState(GlobalPartnerContentTypes.BasicInfo)
  const [data, setData] = useState<IGlobalPartnerDetails[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [filters, setFilters] = useState(INITIAL_FILTERS)
  const [shouldShowAddPartnersDialog, setShouldShowAddPartnersDialog] = useState(false)
  const [offerings, setOfferings] = useState<IProgramType[]>([])
  const [showPasteFromClipboard, setShowPasteFromClipboard] = useState(false)

  const crud = getJbpGlobalPartnerDetails(formData.globalPartnerId)

  const totalPartners = useMemo(() => {
    const roots = data.filter((it) => it.ParentId === null)
    return roots.reduce((acc, it) => acc + it.PartnersCount, 0)
  }, [data])

  /** -------------------- Methods ----------------------- */
  const showAddPartnersDialog = useCallback(() => {
    setShouldShowAddPartnersDialog(true)
  }, [])

  const closeAddPartnersDialog = useCallback(() => {
    setShouldShowAddPartnersDialog(false)
  }, [])

  const openPasteFromClipboardModal = useCallback(() => {
    setShowPasteFromClipboard(true)
  }, [])

  const closePasteFromClipboardModal = useCallback(() => {
    setShowPasteFromClipboard(false)
  }, [])

  const onCheckedItemsSave = useCallback(
    async (partners: IAttachedPartner[]) => {
      const partnerIds = partners.map((partner) => partner.PartnerId)
      await addPartnersToGlobalPartner({
        globalPartnerId: formData.globalPartnerId,
        partnerIds,
        currentFYear,
      })
      closeAddPartnersDialog()
    },
    [closeAddPartnersDialog, formData.globalPartnerId, currentFYear]
  )

  const handleTabChange = useCallback(
    (tab: GlobalPartnerContentTypes) => {
      setContentType(tab)
      onTabChange(tab)
    },
    [onTabChange]
  )

  const fetchData = useCallback(async () => {
    setIsLoading(true)

    const response = await crud.fetch()
    setData(response.items)

    setIsLoading(false)
  }, [crud])

  const handleFilterChange = useCallback(
    (newFilters: IFilters) => {
      crud.removeQueries(true)
      setFilters(newFilters)
    },
    [crud]
  )
  /** -------------------- Effects ----------------------- */
  useEffect(() => {
    crud.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'GlobalPartnerId',
          operator: Operator.EQUAL,
          value: formData.globalPartnerId,
        },
        {
          field: 'Pbms',
          operator: Operator.EQUAL,
          value: filters.pbmFilter.join(','),
        },
        {
          field: 'Geo',
          operator: Operator.EQUAL,
          value: filters.geoMappingsFilter.join(','),
        },
        {
          field: 'Products',
          operator: Operator.EQUAL,
          value: filters.productTreeFilter.join(','),
        },
        {
          field: 'PartnerIds',
          operator: Operator.EQUAL,
          value: filters.partnerIdFilter.join(','),
        },
        {
          field: 'Managers',
          operator: Operator.EQUAL,
          value: filters.managerFilter.join(','),
        },
      ],
    })

    fetchData()

    return crud.subscribe('change', () => {
      fetchData()
    })
  }, [formData.globalPartnerId, fetchData, crud, fYear, globalPartnerType, filters])

  useEffect(() => {
    setIsLoading(true)
    const offeringsCrud = CrudStore.get(
      CrudStoreItemPartner360.JBPOfferings,
      'global-partner-offerings'
    )

    offeringsCrud.setPaging({ page: 0, pageSize: 100 })
    offeringsCrud.setFilter(
      getOfferingsFilter({
        rtms: GLOBAL_PARTNER_TYPE_OPTIONS,
        fYear,
      })
    )

    offeringsCrud.fetch().then((offeringsRes) => {
      const offeringsForCurrentRtm: IFormOfferings[] = offeringsRes.items
        .filter((item) => item.PartnerType === formData.rtm)
        .map((el) => ({ ...el, Links: JSON.parse(el.Links || '') || [] }))

      const enabledOfferings = isNewGlobalPartner
        ? offeringsForCurrentRtm.map((item) => item.Offering)
        : formData.enabledOfferings

      const data = mapPartner360ProgramTypes(offeringsForCurrentRtm, enabledOfferings).filter(
        (it) => !it.isDeprecated
      )
      setOfferings(data)
      setIsLoading(false)
    })
  }, [formData.rtm, formData.enabledOfferings, isNewGlobalPartner, fYear])

  useEffect(() => {
    if (!isNewGlobalPartner) {
      setContentType(GlobalPartnerContentTypes.List)
    }
  }, [isNewGlobalPartner])

  const dispatch = useAppDispatch()
  useEffect(() => {
    dispatch(requestP360PartnerFlags())
  }, [dispatch])

  return (
    <div
      className={cn(styles.root, {
        [styles.withScroll]: contentType === GlobalPartnerContentTypes.EntityManagement,
      })}
    >
      <LoaderOverlay show={isLoading || isFlagsLoading} />
      {!isNewGlobalPartner && (
        <div className={styles.contentTypesWrapper}>
          <ContentTypeButtons
            value={contentType}
            translationKeyPrefix={TRANSLATION_KEYS.globalPartnerDetails.contentTypeButtonsPrefix}
            onContentTypeChange={handleTabChange}
            isNewGlobalPartner={isNewGlobalPartner}
          />
        </div>
      )}

      <RenderWhen condition={contentType === GlobalPartnerContentTypes.List}>
        <div className={styles.addMoreWrapper}>
          <Translation
            span
            text={TRANSLATION_KEYS.globalPartnerDetails.totalPartners}
            replaceParams={[
              {
                key: '{totalCount}',
                value: totalPartners.toString(),
              },
            ]}
          />
          <Button onClick={showAddPartnersDialog} className={styles.addMoreButton}>
            <Translation text={TRANSLATION_KEYS.globalPartnerDetails.addMoreButton} />
          </Button>
          <Button className={styles.btns__btn} onClick={openPasteFromClipboardModal}>
            <Translation text={TRANSLATION_KEYS.globalPartnerDetails.openPasteFromClipboard} />
          </Button>
          {hqPartnerId && (
            <div className={styles.hqPartnerIdWarning}>
              <Translation text={TRANSLATION_KEYS.globalPartnerDetails.hqPartnerIdWarning} />
            </div>
          )}
        </div>
      </RenderWhen>

      <RenderWhen condition={contentType !== GlobalPartnerContentTypes.BasicInfo}>
        <div className={styles.filtersWrapper}>
          <FiltersContainer onFilterChange={handleFilterChange} data={data} rtm={formData.rtm} />
        </div>
      </RenderWhen>

      <RenderWhen condition={contentType === GlobalPartnerContentTypes.BasicInfo}>
        <BasicInfo
          initialData={initialData}
          formData={formData}
          isValid={isValid}
          offerings={offerings}
          onClose={onClose}
          currentFYear={currentFYear}
          onContentTypeChange={handleTabChange}
          isNewGlobalPartner={isNewGlobalPartner}
          globalPartnerType={globalPartnerType}
        />
      </RenderWhen>

      <RenderWhen condition={contentType === GlobalPartnerContentTypes.List}>
        <div className={styles.tableContent}>
          <TableContent
            globalPartnerType={globalPartnerType}
            globalPartnerId={formData.globalPartnerId}
            globalPartnerCurrentFYearTargetsNet={globalPartnerCurrentFYearTargetsNet}
            globalPartnerCurrentFYearTargetsTNet={globalPartnerCurrentFYearTargetsTNet}
            globalPartnerCurrentFYearTargetsUTNet={globalPartnerCurrentFYearTargetsUTNet}
            globalPartnerCurrentFYearTargetsNdp={globalPartnerCurrentFYearTargetsNdp}
            data={data}
            isLoading={isLoading}
            valueType={valueType}
            onChangeValueType={onChangeValueType}
            hqPartnerId={hqPartnerId}
            fYear={fYear}
            globalPartnerName={formData.globalPartnerId}
          />
        </div>
      </RenderWhen>

      <RenderWhen condition={contentType === GlobalPartnerContentTypes.EntityManagement}>
        <EntityManagement
          data={data}
          globalPartnerType={globalPartnerType}
          globalPartnerId={formData.globalPartnerId}
        />
      </RenderWhen>

      {shouldShowAddPartnersDialog && (
        <AddPartnersModal
          globalPartnerId={formData.globalPartnerId}
          fYear={fYear}
          onCheckedItemsSave={onCheckedItemsSave}
          onModalClose={closeAddPartnersDialog}
          globalPartnerType={globalPartnerType}
        />
      )}
      <PasteFromClipboard
        globalPartnerId={formData.globalPartnerId}
        show={showPasteFromClipboard}
        onClose={closePasteFromClipboardModal}
        fYear={currentFYear}
      />
    </div>
  )
}
